import React, { useCallback, useEffect, useRef, useState } from "react";
import { Building2, Plus, CheckCircle2, Trash2, Star, X, ImagePlus, Video, Crosshair } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccLabel, ccCard } from "./care/ui";
import ImageCropModal from "./lib/ImageCrop";
import FocalPointModal from "./lib/FocalPoint";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", city: "", state: "", description: "", enroll_url: "", photos: [], video: "", focals: {} };

// Client-side image downscale (max 1600px) keeps phone uploads fast
const resizeImage = (file, max = 1600) =>
  new Promise((resolve) => {
    if (!file.type.startsWith("image/")) return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      if (scale >= 1) {
        URL.revokeObjectURL(url);
        return resolve(file);
      }
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          resolve(blob ? new File([blob], file.name, { type: file.type }) : file);
        },
        file.type,
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

const uploadMedia = async (file) => {
  const resized = await resizeImage(file);
  const fd = new FormData();
  fd.append("file", resized);
  const res = await fetch(`${API}/api/store/admin/media`, { method: "POST", credentials: "include", body: fd });
  const body = await res.json();
  if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Upload failed");
  return body;
};

const mediaSrc = (path) => (path?.startsWith("http") ? path : `${API}/api/store/media/${path}`);

// Upload-only media picker: up to 2 photos + 1 video, phone-friendly, instant previews
const MediaEditor = ({ photos, video, onPhotos, onVideo, focals = {}, onFocals, prefix }) => {
  const [busy, setBusy] = useState(null);
  const [cropFile, setCropFile] = useState(null);
  const [focalFor, setFocalFor] = useState(null);
  const photoRef = useRef(null);
  const videoRef = useRef(null);

  const pick = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (kind === "photo" && photos.length >= 2) return toast.error("Up to 2 photos per listing.");
    if (kind === "video" && video) return toast.error("Only 1 video per listing.");
    // Photos go through the cropper first for perfect display
    if (kind === "photo") {
      setCropFile(file);
      return;
    }
    setBusy(kind);
    try {
      const body = await uploadMedia(file);
      onVideo(body.path);
      toast.success("Video uploaded.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const uploadCropped = async (cropped) => {
    setCropFile(null);
    setBusy("photo");
    try {
      const body = await uploadMedia(cropped);
      onPhotos([...photos, body.path]);
      setFocalFor(body.path);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid={`${prefix}-media`}>
      <label className={ccLabel}>Photos (up to 2) & Video (1)</label>
      <div className="flex flex-wrap gap-2.5">
        {photos.map((p, i) => (
          <div key={p} className="relative w-24 h-24 rounded-2xl overflow-hidden border border-[#FCE6E9] bg-[#FFF9F2]" data-testid={`${prefix}-photo-preview-${i}`}>
            <img src={mediaSrc(p)} alt={`Listing photo ${i + 1}`} className="w-full h-full object-cover" style={focals[p] ? { objectPosition: `${focals[p].x}% ${focals[p].y}%` } : undefined} />
            <button type="button" onClick={() => onPhotos(photos.filter((x) => x !== p))} data-testid={`${prefix}-photo-remove-${i}`} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-rose-400 flex items-center justify-center shadow-sm">
              <X className="w-3 h-3" />
            </button>
            <button type="button" onClick={() => setFocalFor(p)} title="Adjust focus point" data-testid={`${prefix}-photo-focus-${i}`} className="absolute bottom-1 left-1 w-6 h-6 rounded-full bg-white/90 text-[#3D7E9E] flex items-center justify-center shadow-sm">
              <Crosshair className="w-3 h-3" />
            </button>
          </div>
        ))}
        {photos.length < 2 && (
          <button type="button" onClick={() => photoRef.current?.click()} disabled={busy !== null} data-testid={`${prefix}-photo-add`} className="w-24 h-24 rounded-2xl border-2 border-dashed border-[#F8C8DC] bg-[#FFF9F2] text-[#E5A0BC] flex flex-col items-center justify-center gap-1 text-[9px] font-extrabold uppercase hover:bg-[#FCE6E9] transition-colors disabled:opacity-50">
            <ImagePlus className="w-4 h-4" />
            {busy === "photo" ? "Uploading…" : "Add Photo"}
          </button>
        )}
        {video ? (
          <div className="relative w-40 h-24 rounded-2xl overflow-hidden border border-[#FCE6E9] bg-black" data-testid={`${prefix}-video-preview`}>
            <video src={mediaSrc(video)} className="w-full h-full object-cover" muted playsInline />
            <button type="button" onClick={() => onVideo("")} data-testid={`${prefix}-video-remove`} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-rose-400 flex items-center justify-center shadow-sm">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => videoRef.current?.click()} disabled={busy !== null} data-testid={`${prefix}-video-add`} className="w-40 h-24 rounded-2xl border-2 border-dashed border-[#B3E0F2] bg-[#FFF9F2] text-[#3D7E9E] flex flex-col items-center justify-center gap-1 text-[9px] font-extrabold uppercase hover:bg-[#E6F2FA] transition-colors disabled:opacity-50">
            <Video className="w-4 h-4" />
            {busy === "video" ? "Uploading…" : "Add Video"}
          </button>
        )}
      </div>
      <input ref={photoRef} type="file" accept="image/*" className="hidden" data-testid={`${prefix}-photo-input`} onChange={(e) => pick(e, "photo")} />
      <input ref={videoRef} type="file" accept="video/*" className="hidden" data-testid={`${prefix}-video-input`} onChange={(e) => pick(e, "video")} />
      {cropFile && <ImageCropModal file={cropFile} defaultAspect={4 / 3} onCancel={() => setCropFile(null)} onDone={uploadCropped} />}
      {focalFor && (
        <FocalPointModal
          src={mediaSrc(focalFor)}
          initial={focals[focalFor] || { x: 50, y: 50 }}
          onCancel={() => setFocalFor(null)}
          onDone={(pt) => {
            onFocals({ ...focals, [focalFor]: pt });
            setFocalFor(null);
            toast.success("Focus point saved.");
          }}
        />
      )}
      <p className="text-[10px] font-bold text-[#9CA3AF] mt-1.5">Upload straight from your phone — crop to frame, auto-resized for fast loading.</p>
    </div>
  );
};

const FeaturedForm = ({ values, onChange, onSubmit, onCancel, busy, prefix }) => (
  <form onSubmit={onSubmit} className="space-y-3 p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl" data-testid={`${prefix}-form`}>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className={ccLabel}>Daycare Name *</label>
        <input required value={values.name} onChange={(e) => onChange({ ...values, name: e.target.value })} className={ccInput} data-testid={`${prefix}-name`} />
      </div>
      <div>
        <label className={ccLabel}>Enroll / Profile URL</label>
        <input value={values.enroll_url} onChange={(e) => onChange({ ...values, enroll_url: e.target.value })} placeholder="https://…" className={ccInput} data-testid={`${prefix}-url`} />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ccLabel}>Phone (optional)</label>
            <input value={values.phone || ""} onChange={(e) => onChange({ ...values, phone: e.target.value })} placeholder="(555) 555-1234" className={ccInput} data-testid={`${prefix}-phone`} />
          </div>
          <div>
            <label className={ccLabel}>Public Email (optional)</label>
            <input value={values.email || ""} onChange={(e) => onChange({ ...values, email: e.target.value })} placeholder="hello@daycare.com" className={ccInput} data-testid={`${prefix}-email`} />
          </div>
        </div>
      </div>
      <div>
        <label className={ccLabel}>City</label>
        <input value={values.city} onChange={(e) => onChange({ ...values, city: e.target.value })} className={ccInput} data-testid={`${prefix}-city`} />
      </div>
      <div>
        <label className={ccLabel}>State</label>
        <input value={values.state} onChange={(e) => onChange({ ...values, state: e.target.value })} className={ccInput} data-testid={`${prefix}-state`} />
      </div>
    </div>
    <div>
      <label className={ccLabel}>Brief Description</label>
      <textarea rows={2} value={values.description} onChange={(e) => onChange({ ...values, description: e.target.value })} className={ccInput} data-testid={`${prefix}-description`} />
    </div>
    <MediaEditor photos={values.photos || []} video={values.video || ""} onPhotos={(ph) => onChange({ ...values, photos: ph })} onVideo={(v) => onChange({ ...values, video: v })} focals={values.focals || {}} onFocals={(fo) => onChange({ ...values, focals: fo })} prefix={prefix} />
    <div className="flex gap-2.5">
      <button type="submit" disabled={busy} className="px-5 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-extrabold transition-all disabled:opacity-50" data-testid={`${prefix}-submit`}>
        {busy ? "Saving..." : "Save Listing"}
      </button>
      <button type="button" onClick={onCancel} className="px-5 py-2.5 bg-white border border-[#FCE6E9] text-[#6B7280] rounded-full text-xs font-bold" data-testid={`${prefix}-cancel`}>
        Cancel
      </button>
    </div>
  </form>
);

export default function MasterFeatured() {
  const [featured, setFeatured] = useState([]);
  const [pending, setPending] = useState(0);
  const [tab, setTab] = useState("listings");
  const [requests, setRequests] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [stats, setStats] = useState({});

  const loadStats = useCallback(() => {
    fetch(`${API}/api/master/featured-stats`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { stats: {} }))
      .then((d) => setStats(d.stats || {}))
      .catch(() => {});
  }, []);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/api/master/featured-daycares`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { featured: [], pending_requests: 0 }))
      .then((d) => {
        setFeatured(d.featured || []);
        setPending(d.pending_requests || 0);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    load();
    loadStats();
    const iv = setInterval(loadStats, 30000);
    return () => clearInterval(iv);
  }, [load, loadStats]);

  const loadRequests = useCallback(() => {
    fetch(`${API}/api/master/featured-requests`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { requests: [] }))
      .then((d) => setRequests(d.requests || []))
      .catch(() => setRequests([]));
  }, []);
  useEffect(() => {
    if (tab === "requests") loadRequests();
  }, [tab, loadRequests]);

  const call = async (path, method, body) => {
    const res = await fetch(`${API}${path}`, {
      method,
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Request failed");
    return data;
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await call("/api/master/featured-daycares", "POST", form);
      toast.success(`${form.name} is now featured on the landing page.`);
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await call(`/api/master/featured-daycares/${editId}`, "PATCH", editForm);
      toast.success("Listing updated — live on the landing scroll.");
      setEditId(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await call(`/api/master/featured-daycares/${item.id}`, "PATCH", { active: !item.active });
      toast.success(item.active ? `${item.name} hidden from landing.` : `${item.name} is live on landing.`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Permanently remove ${item.name} from featured listings?`)) return;
    try {
      await call(`/api/master/featured-daycares/${item.id}`, "DELETE");
      toast.success(`${item.name} removed.`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const review = async (rid, action, name) => {
    try {
      await call(`/api/master/featured-requests/${rid}/${action}`, "POST");
      toast.success(action === "approve" ? `${name} approved — now live on the landing scroll.` : `${name} request rejected.`);
      loadRequests();
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className={`${ccCard} p-6`} data-testid="featured-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#FFF2D1] flex items-center justify-center">
            <Star className="w-4 h-4 text-[#D9A93E]" />
          </span>
          Featured Daycares
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setTab("listings")} data-testid="featured-tab-listings" className={`px-4 py-2 rounded-full text-xs font-extrabold transition-colors ${tab === "listings" ? "bg-[#374151] text-white" : "bg-white border border-[#FCE6E9] text-[#6B7280]"}`}>
            Listings ({featured.length})
          </button>
          <button onClick={() => setTab("requests")} data-testid="featured-tab-requests" className={`px-4 py-2 rounded-full text-xs font-extrabold transition-colors flex items-center gap-1.5 ${tab === "requests" ? "bg-[#374151] text-white" : "bg-white border border-[#FCE6E9] text-[#6B7280]"}`}>
            Requests
            {pending > 0 && (
              <span className="px-1.5 py-0.5 bg-rose-500 text-white text-[9px] font-black rounded-full" data-testid="featured-requests-badge">
                {pending}
              </span>
            )}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">
        Curated site-filler listings shown in the landing page scroll, each with a brief description, external enroll link, and a shareable profile page.
      </p>

      {tab === "listings" ? (
        <div className="space-y-3">
          {!showForm && (
            <button onClick={() => setShowForm(true)} data-testid="featured-add-btn" className="flex items-center gap-1.5 px-4 py-2.5 bg-[#E2F4E1] hover:bg-[#C4E5C2] text-[#2F6031] rounded-full text-xs font-extrabold transition-all">
              <Plus className="w-3.5 h-3.5" /> Add Featured Daycare
            </button>
          )}
          {showForm && <FeaturedForm values={form} onChange={setForm} onSubmit={add} onCancel={() => setShowForm(false)} busy={busy} prefix="featured-add" />}
          {featured.length === 0 && !showForm && <p className="text-xs text-[#9CA3AF] font-medium py-2" data-testid="featured-empty">No featured daycares yet — add one to fill the landing scroll.</p>}
          {featured.map((item) => (
            <div key={item.id} className="p-3.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl" data-testid={`featured-row-${item.id}`}>
              {editId === item.id ? (
                <FeaturedForm values={editForm} onChange={setEditForm} onSubmit={saveEdit} onCancel={() => setEditId(null)} busy={busy} prefix="featured-edit" />
              ) : (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[#FFF2D1] flex items-center justify-center text-[#D9A93E] text-xs font-black font-heading shrink-0 overflow-hidden">
                      {item.photos?.[0] ? <img src={mediaSrc(item.photos[0])} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-extrabold truncate">{item.name}</p>
                      <p className="text-[11px] text-[#9CA3AF] truncate">
                        {[item.city, item.state].filter(Boolean).join(", ") || "No location"}
                        {item.enroll_url ? " · enroll link set" : ""}
                      </p>
                      <p className="text-[10px] font-bold text-[#D9A93E]" data-testid={`featured-stats-${item.id}`}>
                        {stats[item.id]?.views ?? 0} views · {stats[item.id]?.clicks ?? 0} enroll clicks · {stats[item.id]?.ctr ?? 0}% CTR
                        <span className="text-[#9CA3AF] font-semibold"> (30d: {stats[item.id]?.views_30d ?? 0}/{stats[item.id]?.clicks_30d ?? 0})</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`px-2.5 py-1 text-[10px] font-extrabold uppercase rounded-full border ${item.active ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : "bg-gray-50 border-gray-200 text-gray-400"}`} data-testid={`featured-status-${item.id}`}>
                      {item.active ? "Live" : "Hidden"}
                    </span>
                    <button onClick={() => toggleActive(item)} data-testid={`featured-toggle-${item.id}`} className="px-3 py-1.5 bg-white border border-[#E6F2FA] text-[#3D7E9E] rounded-full text-[10px] font-extrabold hover:bg-[#E6F2FA] transition-colors">
                      {item.active ? "Hide" : "Publish"}
                    </button>
                    <button onClick={() => { setEditId(item.id); setEditForm({ name: item.name, city: item.city || "", state: item.state || "", description: item.description || "", enroll_url: item.enroll_url || "", photos: item.photos || [], video: item.video || "", focals: item.focals || {} }); }} data-testid={`featured-edit-${item.id}`} className="px-3 py-1.5 bg-white border border-[#FCE6E9] text-[#6B7280] rounded-full text-[10px] font-extrabold hover:bg-[#FCE6E9] transition-colors">
                      Edit
                    </button>
                    <button onClick={() => remove(item)} data-testid={`featured-delete-${item.id}`} className="w-7 h-7 rounded-full bg-rose-50 border border-rose-200 text-rose-400 flex items-center justify-center hover:bg-rose-100 transition-colors" title="Delete listing">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3" data-testid="featured-requests-list">
          {requests === null ? (
            <p className="text-xs text-[#9CA3AF] font-medium py-2">Loading…</p>
          ) : requests.length === 0 ? (
            <p className="text-xs text-[#9CA3AF] font-medium py-2" data-testid="featured-requests-empty">No pending listing requests.</p>
          ) : (
            requests.map((r) => (
              <div key={r.id} className="p-3.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl" data-testid={`featured-request-${r.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-xs font-extrabold">{r.name}</p>
                    <p className="text-[11px] text-[#9CA3AF]">
                      {[r.city, r.state].filter(Boolean).join(", ")} · {r.contact_name} · {r.email}
                    </p>
                    {r.description && <p className="text-[11px] text-[#6B7280] mt-1 leading-snug">{r.description}</p>}
                    {r.enroll_url && (
                      <a href={r.enroll_url} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-[#3D7E9E] hover:underline break-all">
                        {r.enroll_url}
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => review(r.id, "approve", r.name)} data-testid={`featured-approve-${r.id}`} className="flex items-center gap-1 px-3 py-1.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-[10px] font-extrabold hover:bg-[#C4E5C2] transition-colors">
                      <CheckCircle2 className="w-3 h-3" /> Approve
                    </button>
                    <button onClick={() => review(r.id, "reject", r.name)} data-testid={`featured-reject-${r.id}`} className="flex items-center gap-1 px-3 py-1.5 bg-rose-50 border border-rose-200 text-rose-500 rounded-full text-[10px] font-extrabold hover:bg-rose-100 transition-colors">
                      <X className="w-3 h-3" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
